import FormattedDatePickerReportsV3 from "../FormattedDatePickerReportsV3";
import { parseThresholdInput } from "../utils/rangeGroupingUtils";
import CircleToggleButton from "./CircleToggleButton";
import RangeGroupingLabelEditor from "./RangeGroupingLabelEditor";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import {
  RANGE_GROUPING_DATE_FIELD_MIN_WIDTH,
  RANGE_GROUPING_ENTRY_INDENT,
  RANGE_GROUPING_LABEL_MIN_HEIGHT,
  RANGE_VALUE_ENTRY_ATTRIBUTE,
  RANGE_VALUE_TYPE_DATE,
  RANGE_VALUE_TYPE_STRING,
} from "components/reportsV3/types/constants";
import * as labels from "config.labels";
import React, { useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const RangeGroupingRow = ({
  index,
  value,
  valueType,
  valuePrefix,
  valueSuffix,
  errorText,
  isLast,
  labelText,
  defaultLabel,
  labelOverride,
  isDuplicate,
  showLabelLine,
  canInsert,
  autoFocus,
  onChange,
  onDelete,
  onInsert,
  onLabelChange,
  nullTarget,
  isNullTarget,
  onNullTargetClick,
  onValueTab,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const [anchor, setAnchor] = useState(null);
  const deleteLabel = translateLabels(labels.RANGE_GROUPING_DIALOG_DELETE_ROW, {
    default: "Delete row",
  });
  const insertLabel = translateLabels(labels.RANGE_GROUPING_DIALOG_INSERT_ROW, {
    default: "Insert row",
  });
  const editLabel = translateLabels(labels.RANGE_GROUPING_DIALOG_EDIT_LABEL, {
    default: "Edit label",
  });
  const nullTargetLabel = translateLabels(
    labels.RANGE_GROUPING_DIALOG_NULL_TARGET,
    { default: "Put rows with no value here" }
  );
  const isString = valueType === RANGE_VALUE_TYPE_STRING;
  const entryText = isString
    ? translateLabels(labels.RANGE_GROUPING_DIALOG_OTHERWISE_MATCHES, {
        default: "Otherwise Matches:",
      })
    : translateLabels(labels.RANGE_GROUPING_DIALOG_UP_TO, {
        default: "Up to:",
      });
  const hasNullTargetButton = nullTarget !== null && nullTarget !== undefined;
  const nullTargetButton = hasNullTargetButton && (
    <CircleToggleButton
      active={Boolean(isNullTarget)}
      title={nullTargetLabel}
      onClick={() => onNullTargetClick(nullTarget)}
    />
  );

  const handleValueKeyDown = (event) => {
    if (
      event.key === "Tab" &&
      !event.shiftKey &&
      event.target instanceof HTMLInputElement
    ) {
      event.preventDefault();
      onValueTab?.(index);
    }
  };

  const handleLabelKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setAnchor(event.currentTarget);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
      {showLabelLine && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tooltip title={editLabel}>
              <Typography
                variant={"body2"}
                role={"button"}
                tabIndex={0}
                aria-label={editLabel}
                color={isDuplicate ? "error" : undefined}
                sx={{
                  cursor: "pointer",
                  minHeight: RANGE_GROUPING_LABEL_MIN_HEIGHT,
                }}
                onClick={(event) => setAnchor(event.currentTarget)}
                onKeyDown={handleLabelKeyDown}
              >
                {labelText
                  ? translateLabels(labels.RANGE_GROUPING_DIALOG_LABEL_LINE, {
                      default: "{label}:",
                      replacements: { label: labelText },
                    })
                  : ""}
              </Typography>
            </Tooltip>
            {nullTargetButton}
          </Box>
          <RangeGroupingLabelEditor
            open={Boolean(anchor)}
            anchorEl={anchor}
            initialText={labelOverride ?? ""}
            placeholder={defaultLabel}
            onApply={(text) => onLabelChange(index, text)}
            onClose={() => setAnchor(null)}
          />
        </>
      )}
      {!showLabelLine && hasNullTargetButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            minHeight: RANGE_GROUPING_LABEL_MIN_HEIGHT,
          }}
        >
          {nullTargetButton}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          pl: RANGE_GROUPING_ENTRY_INDENT,
        }}
      >
        <Typography variant={"body2"} sx={{ pt: 1 }}>
          {entryText}
        </Typography>
        <Box
          sx={{ display: "contents" }}
          {...{ [RANGE_VALUE_ENTRY_ATTRIBUTE]: true }}
          onKeyDown={handleValueKeyDown}
        >
          {isString ? (
            <TextField
              size={"small"}
              variant={"outlined"}
              value={value}
              error={Boolean(errorText)}
              helperText={errorText || undefined}
              autoFocus={autoFocus}
              onChange={(e) => onChange(index, e.target.value)}
            />
          ) : valueType === RANGE_VALUE_TYPE_DATE ? (
            <FormattedDatePickerReportsV3
              type={"date"}
              compact
              clearable={false}
              sx={{
                minWidth: RANGE_GROUPING_DATE_FIELD_MIN_WIDTH,
                flexShrink: 0,
              }}
              value={value}
              helperText={errorText || undefined}
              autoFocus={autoFocus}
              slotProps={{ textField: { error: Boolean(errorText) } }}
              onChangeDate={(date) => onChange(index, date || "")}
            />
          ) : (
            <PrettyNumberTextField
              size={"small"}
              variant={"outlined"}
              value={parseThresholdInput(value).value}
              stringValue={value}
              error={Boolean(errorText)}
              helperText={errorText || undefined}
              onChange={(e) => onChange(index, e.target.value)}
              autoFocus={autoFocus}
              InputProps={{
                startAdornment: valuePrefix ? (
                  <InputAdornment position={"start"}>
                    {valuePrefix}
                  </InputAdornment>
                ) : undefined,
                endAdornment: valueSuffix ? (
                  <InputAdornment position={"end"}>
                    {valueSuffix}
                  </InputAdornment>
                ) : undefined,
              }}
            />
          )}
        </Box>
        {!isLast && (
          <Box sx={{ display: "flex", pt: 0.5 }}>
            <Tooltip title={deleteLabel}>
              <IconButton
                size={"small"}
                aria-label={deleteLabel}
                onClick={() => onDelete(index)}
              >
                <ClearIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={insertLabel}>
              <span>
                <IconButton
                  size={"small"}
                  aria-label={insertLabel}
                  disabled={!canInsert}
                  onClick={() => onInsert(index)}
                >
                  <AddIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RangeGroupingRow;
